import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, Loader2, X, Check } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Pager, paginate } from "../Pager";
import { formatMoney, CURRENCIES } from "../../../lib/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BILLINGS = [{ value: "one_off", label: "One-off" }, { value: "monthly", label: "Per month" }, { value: "yearly", label: "Per year" }];
const SUFFIX = { one_off: "", monthly: "/mo", yearly: "/yr" };
const money = (v, cur = "AUD") => formatMoney(v, cur);

export default function ProductLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", description: "", default_price: 0, default_billing: "monthly", active: true, sort_order: 100, prices: { AUD: 0 } });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const paged = paginate(items, page);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/quotes/catalogue`, { params: { include_inactive: true } }); setItems(r.data.catalogue || []); }
    catch (e) { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ code: "", name: "", description: "", default_price: 0, default_billing: "monthly", active: true, sort_order: (items.length + 1) * 10, prices: { AUD: "" } }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, description: it.description || "", prices: { ...(it.prices || {}) } }); setOpen(true); };

  const setPrice = (code, v) => setForm((f) => ({ ...f, prices: { ...(f.prices || {}), [code]: v.replace(/[^0-9.]/g, "") } }));
  const cleanPrices = (prices) => CURRENCIES.reduce((acc, c) => {
    const v = (prices || {})[c.code];
    if (v === "" || v === null || v === undefined) return acc;
    return { ...acc, [c.code]: Number(v) || 0 };
  }, {});

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and name are required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/quotes/catalogue/${encodeURIComponent(editing.code)}`, {
          name: form.name, description: form.description,
          prices: cleanPrices(form.prices),
          default_price: Number((form.prices || {}).AUD) || 0,
          default_billing: form.default_billing, active: form.active, sort_order: Number(form.sort_order) || 100,
        });
      } else {
        await axios.post(`${API}/quotes/catalogue`, { ...form, code: form.code.toUpperCase(), prices: cleanPrices(form.prices), default_price: Number((form.prices || {}).AUD) || 0, sort_order: Number(form.sort_order) || 100 });
      }
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete product ${it.code}?`)) return;
    try { await axios.delete(`${API}/quotes/catalogue/${encodeURIComponent(it.code)}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const toggleActive = async (it) => {
    try { await axios.put(`${API}/quotes/catalogue/${encodeURIComponent(it.code)}`, { active: !(it.active !== false) }); load(); }
    catch (e) { toast.error("Failed to update"); }
  };

  return (
    <div className="space-y-4" data-testid="product-library">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFF8D6] flex items-center justify-center"><Package className="w-5 h-5 text-[#8A6D00]" /></div>
          <div>
            <h2 className="heading text-2xl text-[#1A1A1A] tracking-tight">Product Library</h2>
            <p className="text-sm text-[#59554D]">Products and services that can be added to quotes. Default prices can be overridden per quote.</p>
          </div>
        </div>
        <Button onClick={openNew} className="h-9 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] uppercase text-[11px] tracking-wide font-semibold" data-testid="product-new-btn"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>
      </div>

      <Card className="bg-white border-[#DAD5CA] shadow-card rounded-xl overflow-hidden p-0">
        <CardContent className="p-0">
          <div className="grid items-center px-4 py-2.5 bg-[#F6F4EF] border-b border-[#DAD5CA] text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium" style={{ gridTemplateColumns: "80px 1fr 120px 110px 70px 80px" }}>
            <span>Code</span><span>Name</span><span className="text-right">Default price</span><span>Billing</span><span className="text-center">Active</span><span className="text-right">Actions</span>
          </div>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => <div key={i} className="px-4 py-3 border-b border-[#F0EDE6]"><div className="skeleton h-4 w-full" /></div>)
          ) : items.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-[#8A857A]">No products yet.</div>
          ) : paged.map((it) => (
            <div key={it.code} className="grid items-center px-4 py-2.5 border-b border-[#F0EDE6] hover:bg-[#FFFCEF]" style={{ gridTemplateColumns: "80px 1fr 120px 110px 70px 80px" }} data-testid={`product-row-${it.code}`}>
              <span className="mono text-[12px] font-semibold text-[#1A1A1A]">{it.code}</span>
              <div className="min-w-0"><div className="text-[13px] text-[#1A1A1A] truncate">{it.name}</div><div className="text-[11px] text-[#8A857A] truncate">{it.description}</div></div>
              <span className="mono text-[12px] text-right text-[#1A1A1A]" data-testid={`product-prices-${it.code}`}>
                {CURRENCIES.filter((c) => (it.prices || {})[c.code] != null).map((c) => money((it.prices || {})[c.code], c.code)).join(" · ") || money(it.default_price)}
                <span className="text-[#8A857A]">{SUFFIX[it.default_billing]}</span>
              </span>
              <span className="text-[12px] text-[#59554D]">{BILLINGS.find((b) => b.value === it.default_billing)?.label}</span>
              <div className="flex justify-center"><Switch checked={it.active !== false} onCheckedChange={() => toggleActive(it)} data-testid={`product-active-${it.code}`} /></div>
              <div className="flex items-center justify-end gap-0.5">
                <button onClick={() => openEdit(it)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`product-edit-${it.code}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(it)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#FFE9E0] hover:text-[#C23A00]" data-testid={`product-delete-${it.code}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          <Pager page={page} total={items.length} onPage={setPage} />
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) setOpen(false); }}>
        <DialogContent className="bg-white border-[#DAD5CA] text-[#1A1A1A] max-w-lg" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.code}` : "Add product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#59554D]">Code *</label><Input value={form.code} disabled={!!editing} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="bg-[#F4F2ED] border-[#DAD5CA] mono" data-testid="product-code" /></div>
              <div><label className="text-xs text-[#59554D]">Sort order</label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA]" /></div>
            </div>
            <div><label className="text-xs text-[#59554D]">Name *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="product-name" /></div>
            <div><label className="text-xs text-[#59554D]">Description</label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs text-[#59554D]">Price list — one price per currency</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {CURRENCIES.map((c) => (
                    <div key={c.code}>
                      <div className="text-[10px] uppercase tracking-wide text-[#8A857A] mb-0.5">{c.code}</div>
                      <Input value={(form.prices || {})[c.code] ?? ""} placeholder={c.symbol}
                        onChange={(e) => setPrice(c.code, e.target.value)}
                        className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid={`product-price-${c.code}`} />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-[#8A857A] mt-1">Leave a currency blank if you don't sell this product in it. Prices are never converted.</p>
              </div>
              <div><label className="text-xs text-[#59554D]">Billing</label>
                <Select value={form.default_billing} onValueChange={(v) => setForm({ ...form, default_billing: v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="product-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>{BILLINGS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#1A1A1A]"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active (available on quotes)</label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving} className="text-[#59554D]">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="product-save">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} {editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
